import { Option } from '@app/components/various/Fields/Select';
import { AutocompleteField, FieldWrapper, FormGrid } from '@app/components/various/Form';
import { Skeleton } from '@app/components/various/Skeleton';
import { TextEllipsis } from '@app/components/various/TextEllipsis';
import { getMarkets } from '@app/ducks';
import { getPagesMapBySlug, getUniqMarketsNames, getUniqSlugs, isPublishedPage, isPublishedWithChanges } from '@app/logic/pages';
import { useHomepage } from '@app/services/hooks/pages/useHomepage';
import { usePages } from '@app/services/hooks/pages/usePages';
import { isDefined } from '@app/utils/is';
import { Settings } from '@typings';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { HomePagePreview } from './HomaPagePreview';

export const HomePageSection = () => {
  const { t } = useTranslation(['common', 'cms', 'settings', 'products']);
  const { data: homepageData, isLoading: isLoadingHomepage } = useHomepage();
  const homepageSlug = homepageData?.slug;
  const { data: pagesList = [], isLoading: isLoadingPages } = usePages({ refetchOnMount: false, search: '' });
  const publishedPages = React.useMemo(() => pagesList.filter(page => isPublishedPage(page) || isPublishedWithChanges(page)), [pagesList]);
  const allMarkets = useSelector(getMarkets);
  const formMethods = useFormContext<Settings.NavigationFormFields>();
  const { watch, reset, getValues } = formMethods;

  const selectedSlug = watch('slug');

  const slugs = React.useMemo(() => getUniqSlugs(publishedPages), [publishedPages]);
  const selectedPages = getPagesMapBySlug(selectedSlug)(publishedPages);

  React.useEffect(() => {
    reset({ ...getValues(), slug: homepageSlug ?? null });
  }, [homepageSlug, getValues, reset]);

  const defaultPageMarkets = React.useMemo(() => {
    if (!isDefined(selectedSlug)) {
      return allMarkets;
    }

    const selectedPagesMarkets = getUniqMarketsNames(selectedPages);

    return allMarkets.filter(market => !selectedPagesMarkets.includes(market.name));
  }, [selectedPages, allMarkets, selectedSlug]);

  const isLoadingHomepageData = isLoadingPages || isLoadingHomepage;

  return (
    <section className="box space-y-5">
      <h3 className="heading-3">{t('settings:home_page_title')}</h3>
      <p className="text-neutral-40 body-2">{t('settings:home_page_subtitle')}</p>
      <FormGrid rowGap="large">
        <AutocompleteField
          size="large"
          isLoading={isLoadingHomepageData}
          placeholder={t('settings:select_uri')}
          name="slug"
          helpMessage={t('settings:page_slug_description')}
          label={t('settings:home_page_uri')}
          allowClear
        >
          {slugs.map(slug => (
            <Option key={slug} value={slug}>{`/${slug}`}</Option>
          ))}
        </AutocompleteField>

        <FieldWrapper as="span" label={t('settings:published_pages')}>
          <HomePagePreview isLoading={isLoadingHomepageData} selectedSlug={selectedSlug} selectedPages={selectedPages} />
        </FieldWrapper>

        <FieldWrapper as="span" label={t('settings:default_home_page')}>
          {isLoadingHomepageData ?
            <div className="min-h-16 rounded-regular">
              <Skeleton type="rectangle" className="min-h-12" />
            </div>
          : <div className="grid min-h-16 items-center overflow-hidden rounded-regular bg-neutral-96/85 p-3">
              <span className="body-1">{t('products:all_products')}</span>
              <TextEllipsis as="div" className="w-full text-neutral-40 body-5">
                {t('cms:market_other')}: {defaultPageMarkets.map(market => market.name).join(', ') || t('common:none')}
              </TextEllipsis>
            </div>
          }
        </FieldWrapper>
      </FormGrid>
    </section>
  );
};
